import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ConfirmationDialog, IconNames, InlineSVG, LoadingOverlay, Toast, ToastVariants } from '@dotcom/crown-ui';
import { SystemFailureDialog } from '../../components/system-failure-dialog';
import { useAnalyticsContext } from '../../context/analytics-context';
import { leaveAction } from '../../context/analytics-context/analytics-context-types';
import { getSaveStatusMessage } from '../../context/analytics-context/utils/get-save-status-message';
import { setIsSaving, useAppContext } from '../../context/app-context';
import { setIsToasterClosed, setTriggeringElementRef } from '../../context/app-context/app-context-actions';
import { useInitializationDataContext } from '../../context/data-context';
import { CardType, RegionalCodesList } from '../../global-types';
import {
  useConfirmationDialog,
  useEnabledRoutes,
  useFeatureFlags,
  useQueryParams,
  useSaveProject,
  useSaveProjectDialog,
} from '../../hooks';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { useProjectSummary } from '../../hooks/use-project-summary';
import { useSystemFailureDialog } from '../../hooks/use-system-failure-dialog';
import { config } from '../../regional-config';
import { getMonolithUrl, isLoggedIn, removeBeforeUnloadEvent } from '../../utils';
import { savePersonalization } from '../../utils/save-project';
import { EditorHeader } from '../editor-header';
import { PrintOnDemandHeader } from '../print-on-demand-header';
import { Quantity } from '../quantity/quantity';
import { RedirectDialog } from '../redirect-dialog';
import { SaveProjectDialog } from '../save-project-dialog';
import { Toolbar } from '../toolbar/toolbar';
import { LayoutProps } from './layout-types';
import styles from './layout.module.scss';
import { useUIStore } from '../../stores/ui';

export const Layout = ({
  children,
  hideToolbar,
  addToolbarClass,
  addToastClass,
  addLayoutClass,
  onToolbarItemClick,
  editorHeaderProps,
  previewHeaderProps,
  onCloseOverride,
}: LayoutProps) => {
  const shouldHideToolbar = hideToolbar;
  const { appState, appDispatch } = useAppContext();
  const navigate = useNavigate();
  const { toaster, loader, isSystemErrorOpen, isRedirectDialogOpen, exitUrl, isSaving } = appState;
  const isToasterOpen = useUIStore((state) => state.isToasterOpen);
  const toasterTitle = useUIStore((state) => state.toasterTitle);
  const toasterChildren = useUIStore((state) => state.toasterChildren);
  const toasterVariant = useUIStore((state) => state.toasterVariant);
  const closeStoreToaster = useUIStore((state) => state.closeToaster);
  const visibleToaster = toaster.isOpen
    ? toaster
    : {
        isOpen: isToasterOpen,
        title: toasterTitle,
        children: toasterChildren,
        variant: Object.values(ToastVariants).find((variant) => variant === toasterVariant),
      };
  const { saveProject } = useSaveProject();
  const { trackAbandonOrDeleteProject, trackSaveLink } = useAnalyticsContext();
  const {
    initializedDataState: { data: initializedData, isUK, region },
  } = useInitializationDataContext();
  const { IS_EDITOR_HEADER_ENABLED, IS_PREVIEW_TOGGLE_ENABLED } = useFeatureFlags();
  const { pathname } = useLocation();
  const isEditor = useMemo(() => pathname.includes('/edit/') || pathname.includes('/create/'), [pathname]);
  const isAddress = useMemo(() => pathname.includes('/address'), [pathname]);
  const isEcard = useMemo(() => pathname.includes('/ecard-send'), [pathname]);
  const isEnvelope = useMemo(() => pathname.includes('/envelope'), [pathname]);
  const isPreview = useMemo(() => pathname.includes('/preview'), [pathname]);
  const isPreviewHeader = isPreview || !!previewHeaderProps;
  const classes = classNames(
    styles.layout,
    (shouldHideToolbar || (IS_EDITOR_HEADER_ENABLED && isEditor)) && styles['no-toolbar'],
    addLayoutClass,
  );
  const queryParams = useQueryParams();
  const isOneToMany = useIsOneToMany();
  const { handleSystemFailureAction, handleSystemFailureCancel, handleSystemFailureClose } = useSystemFailureDialog();
  const {
    saveProjects: { isSavedProjectsEnabled },
  } = config;
  const { isEnabledRoutes } = useEnabledRoutes();
  const enabledRoutes = ['/edit/', '/address', '/ecard-send', '/envelope', '/preview'];
  const { hasDefaultProjectName } = useProjectSummary();
  const hasUpdatedDraftName = useRef(false);
  const isEditorView = pathname.includes('edit');

  const { isSaveProjectDialogOpen, isAbandoningProject, onCloseDialog, onSignIn, onAbandonProject, onSaveProject } =
    useSaveProjectDialog();

  const { isDialogOpen, setIsDialogOpen, dialogGoToHomePage, handleCloseDialog, discardChanges } =
    useConfirmationDialog();

  const closeToaster = () => {
    if (toaster.isOpen) {
      setIsToasterClosed(appDispatch);
    } else {
      closeStoreToaster();
    }
    document.body.removeAttribute('data-dialog');
  };

  const onCloseApp = useCallback(
    (button: HTMLButtonElement | null) => {
      if (!isEnabledRoutes(enabledRoutes) && isSavedProjectsEnabled) {
        discardChanges();
        return;
      }
      if (button) {
        queryParams.append('exitUrl', exitUrl);
        navigate({ pathname, search: queryParams.toString() }, { replace: true });
      }
    },
    [queryParams, isEnabledRoutes],
  );

  const goToHomePage = () => {
    if (!isEnabledRoutes(enabledRoutes) && isSavedProjectsEnabled) {
      discardChanges();
      return;
    }
    queryParams.append('exitUrl', getMonolithUrl());
    navigate({ pathname, search: queryParams.toString() }, { replace: true });
  };

  const routeActiveView = isPreviewHeader ? 'preview' : 'edit';
  const activeView = editorHeaderProps?.activeView ?? routeActiveView;
  const projectId = initializedData?.project_id || sessionStorage.getItem('lastProjectLoaded');
  const projectTypeCode = initializedData?.project_type_code || sessionStorage.getItem('lastProjectTypeCode');
  const editPath = projectId ? `/card/customization/edit/${projectId}` : pathname;
  const headerSearch = queryParams.toString();
  const navigateToPath = (nextPathname: string, state?: Record<string, unknown>) => {
    if (nextPathname === pathname) {
      return;
    }
    navigate({ pathname: nextPathname, search: headerSearch }, { state });
  };
  const handleViewChange = (view: 'edit' | 'preview') => {
    if (editorHeaderProps?.onViewChange) {
      editorHeaderProps.onViewChange(view);
      return;
    }

    const isFromEdit = view === 'preview' && pathname.includes('/edit/');
    navigateToPath(
      view === 'preview' ? '/card/customization/preview' : editPath,
      isFromEdit ? { fromEdit: true } : undefined,
    );
  };
  const handleAddressClick = () => {
    if (editorHeaderProps?.onAddressClick) {
      editorHeaderProps.onAddressClick();
      return;
    }
    navigate({ pathname: '/card/customization/address', search: headerSearch });
  };
  const addressHeaderSlot =
    (isAddress || isEnvelope) && isOneToMany && projectTypeCode === CardType.POD ? (
      <Quantity />
    ) : (
      <div className={styles['header-empty-slot']} data-testid="editor-header-address-empty-slot" />
    );

  // All this section will be removed once saved projects goes to Prod
  const { t } = useTranslation();

  const dialogOnCloseApp = useCallback((button: HTMLButtonElement | null) => {
    if (button) {
      setTriggeringElementRef(appDispatch, button);
    }
    setIsDialogOpen(true);
  }, []);

  const resolveHasUnsavedChanges = useCallback(async () => {
    if (!editorHeaderProps?.hasUnsavedChanges) {
      return false;
    }

    try {
      return await editorHeaderProps.hasUnsavedChanges();
    } catch {
      // Defensive fallback: if dirty-check fails, protect user edits.
      return true;
    }
  }, [editorHeaderProps?.hasUnsavedChanges]);

  const closeEditorImmediately = useCallback(() => {
    trackAbandonOrDeleteProject(leaveAction.DELETE, 'exit');
    removeBeforeUnloadEvent();
    const destination = exitUrl && exitUrl !== '/' ? exitUrl : document.referrer || '/';
    window.location.assign(destination);
  }, [exitUrl, trackAbandonOrDeleteProject]);

  const handleEditorHeaderClose = useCallback(async () => {
    const hasUnsavedChanges = await resolveHasUnsavedChanges();

    if (!hasUnsavedChanges) {
      closeEditorImmediately();
      return;
    }

    if (isSavedProjectsEnabled) {
      queryParams.append('exitUrl', exitUrl);
      navigate({ pathname, search: queryParams.toString() }, { replace: true });
      return;
    }

    dialogOnCloseApp(null);
  }, [
    resolveHasUnsavedChanges,
    dialogOnCloseApp,
    closeEditorImmediately,
    isSavedProjectsEnabled,
    queryParams,
    exitUrl,
    pathname,
    navigate,
  ]);

  const handleEditorHeaderSave = useCallback(async () => {
    if (isSaving) {
      return;
    }

    if (editorHeaderProps?.onSaveClick) {
      editorHeaderProps.onSaveClick();
      return;
    }

    // If the user has already named their project, save directly (same as SaveProjectLink)
    if (hasUpdatedDraftName.current || !hasDefaultProjectName) {
      setIsSaving(appDispatch, true);
      try {
        await savePersonalization(
          {
            shouldRestoreCanvas: true,
            showLoadingScreen: false,
            generateFrontPreview: false,
            throwOnError: true,
            onSaveSuccess: editorHeaderProps?.onSaveSuccess,
          },
          isEditorView,
          saveProject,
        );
        trackSaveLink('success');
        editorHeaderProps?.onSaveSuccess?.();
      } catch (error) {
        trackSaveLink('fail', getSaveStatusMessage(error));
      } finally {
        setIsSaving(appDispatch, false);
      }
      return;
    }

    // Otherwise, open the save project dialog so the user can name their project
    queryParams.append('saveProjectDialog', 'true');
    navigate({ pathname, search: queryParams.toString() }, { replace: true });
  }, [
    editorHeaderProps?.onSaveClick,
    isSaving,
    hasDefaultProjectName,
    isEditorView,
    saveProject,
    appDispatch,
    queryParams,
    pathname,
    navigate,
    trackSaveLink,
  ]);

  // End of section

  return (
    <div className={classes}>
      {IS_EDITOR_HEADER_ENABLED ? (
        <EditorHeader
          variant={isPreviewHeader ? 'preview' : isAddress || isEcard ? 'address' : isEnvelope ? 'envelope' : 'editor'}
          onClose={onCloseOverride ?? handleEditorHeaderClose}
          onSave={isEditor ? handleEditorHeaderSave : undefined}
          isSaving={isSaving}
          activeView={activeView}
          onViewChange={handleViewChange}
          onAddressClick={handleAddressClick}
          isAddressDisabled={editorHeaderProps?.isAddressDisabled}
          isAddressRestoring={editorHeaderProps?.isAddressRestoring}
          isSaveDisabled={editorHeaderProps?.isSaveDisabled}
          showAddressButton={isEditor}
          showViewToggle={IS_PREVIEW_TOGGLE_ENABLED && isEditor}
          centerSlot={
            <button
              type="button"
              className={styles['crown-logo-button']}
              onClick={onCloseOverride ?? goToHomePage}
              data-testid="editor-header-crown"
              aria-label={`${t('header.hallmarkCrownLogo')}`}
            >
              <InlineSVG
                title={`${t('header.hallmarkCrownLogo')}`}
                description={`${t('header.hallmarkCrownLogo')}`}
                role="img"
                className={isUK ? styles['crown-logo-color-uk'] : undefined}
              />
            </button>
          }
          rightSlot={
            editorHeaderProps?.rightSlot ? (
              <>
                {editorHeaderProps.rightSlot}
                {isAddress ? addressHeaderSlot : null}
              </>
            ) : isAddress ? (
              addressHeaderSlot
            ) : undefined
          }
          previewActions={previewHeaderProps}
          testId="editor-header"
        />
      ) : (
        <PrintOnDemandHeader
          onClose={onCloseOverride ? () => onCloseOverride() : isSavedProjectsEnabled ? onCloseApp : dialogOnCloseApp}
          isEditor={isEditor}
          onReturnToHomePage={onCloseOverride ?? (isSavedProjectsEnabled ? goToHomePage : dialogGoToHomePage)}
          isUK={isUK}
        />
      )}
      <ConfirmationDialog
        id="close-confirmation"
        testId="close-confirmation"
        isOpen={isDialogOpen}
        onCancel={handleCloseDialog}
        onConfirm={discardChanges}
        title={`${t('header.leavePersonalizationTitle')}`}
        confirmLabel={`${t('header.leavePersonalizationActionButtonText')}`}
        cancelLabel={`${t('header.leavePersonalizationCancelButtonText')}`}
        accentIcon={IconNames.CardFeaturesBold}
      >
        {`${t('header.leavePersonalizationContent')}`}
      </ConfirmationDialog>
      {IS_EDITOR_HEADER_ENABLED && isSaveProjectDialogOpen && (
        <SaveProjectDialog
          isOpen={isSaveProjectDialogOpen}
          close={onCloseDialog}
          isLoggedIn={isLoggedIn()}
          isAbandoningProject={isAbandoningProject}
          onSaveProject={onSaveProject}
          onSignIn={onSignIn}
          onAbandonProject={onAbandonProject}
          theme={region || RegionalCodesList.us}
        />
      )}
      {isSystemErrorOpen && (
        <SystemFailureDialog
          isOpen={isSystemErrorOpen}
          actionButton={handleSystemFailureAction}
          cancelButton={handleSystemFailureCancel}
          onClose={handleSystemFailureClose}
        />
      )}
      {isRedirectDialogOpen && <RedirectDialog />}
      <LoadingOverlay isLoading={loader.isLoading} title={loader.title} overlay />
      {visibleToaster.isOpen && (
        <Toast
          variant={visibleToaster.variant}
          isOpen={visibleToaster.isOpen}
          title={visibleToaster.title}
          onClose={closeToaster}
          className={classNames(addToastClass, styles.toast)}
        >
          {visibleToaster.children}
        </Toast>
      )}
      {!shouldHideToolbar && !(IS_EDITOR_HEADER_ENABLED && isEditor) && (
        <Toolbar addClass={addToolbarClass} onToolbarItemClick={onToolbarItemClick} />
      )}

      <div className={styles.content} data-testid="app-view">
        {children}
      </div>
    </div>
  );
};
